<template>
  <ModalBase :show-modal="showModal && !loading" @close-modal="close" :overlay-scrollb="true">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-if="!shareInvoicesState" v-slot:pretitle>
          {{ isEdit ? $gettext('EDIT') : $gettext('ADD') }}
        </template>
        <template v-slot:title>
          <div v-if="!shareInvoicesState" class="d-flex justify-contents-center align-items-center">
            <span v-if="isEdit">{{ contact.name }}</span>
            <span v-if="!isEdit" v-translate>New contact</span>
            <b-badge v-if="isYourCompany" variant="primary" class="header-badge">
              <translate>Your company</translate>
            </b-badge>
          </div>
          <GekkoButton
            v-if="shareInvoicesState"
            @click="toggleShareInvoicesState()"
            :text="$gettext('Back to contact')"
            icon="fa-solid fa-circle-arrow-left"
            class="text-info px-0"
            size="lg"
            variant="link"
          />
        </template>
        <template v-slot:buttons>
          <div v-if="!shareInvoicesState" class="modal-action-bar">
            <GekkoButton
              v-if="isEdit"
              @click="toggleShareInvoicesState()"
              :text="$gettext('Share invoices')"
              icon="fa-solid fa-share-nodes"
              class="text-info"
            />
            <GekkoButton
              v-if="isEdit"
              :text="$gettext('Delete contact')"
              icon="fa-solid fa-trash-can"
              class="text-danger"
              v-gk-confirm="{
                title: $gettext('Delete contact?'),
                message: $gettext('Are you sure you want to delete this contact?'),
                options: {
                  variant: 'danger',
                  confirmText: $gettext('Delete'),
                },
                callback: deleteContact,
              }"
            />
          </div>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <AddEditContact
        v-if="!shareInvoicesState"
        ref="contactform"
        :contactDetails="contact"
        @created="onContactCreated"
        @updated="onContactUpdated"
      />
      <ShareInvoices ref="shareinvoices" :contactDetails="contact" v-if="shareInvoicesState" />
    </template>
    <template v-slot:actions>
      <div
        v-if="isEdit && hasPendingChanges"
        class="flex justify-center align-items-center alert alert-danger archival-banner"
        role="alert"
      >
        <font-awesome-icon icon="fa-solid fa-warning" />
        <translate>You have unsaved changes</translate>
      </div>

      <GekkoButton
        v-if="!shareInvoicesState"
        @click="$refs.contactform.saveForm()"
        :text="$gettext('Save Contact')"
        icon="fa-solid fa-floppy-disk"
        type="submit"
        class="float-right position-relative"
        :loading="isWaitingForResponse"
      />

      <GekkoButton
        v-if="shareInvoicesState"
        @click="$refs.shareinvoices.sendEmail()"
        :text="$gettext('Send Mail')"
        icon="fa-solid fa-paper-plane"
        type="submit"
        class="float-right position-relative"
        :loading="isWaitingForResponse"
      />
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters } from 'vuex'

import TitleBar from '@/components/TitleBar'
import AddEditContact from '@/components/contacts/AddEditContact'
import GekkoButton from '@/components/GekkoButton'
import CustomerService from '@/store/services/customer.service'
import ShareInvoices from '@/components/contacts/ShareInvoices'
import ModalBase from '@/components/base_components/ModalBase'

export default {
  name: 'ContactModal',
  components: { ModalBase, TitleBar, AddEditContact, GekkoButton, ShareInvoices },
  props: {
    showModalOn: {
      type: Array,
      default: () => [],
    },
    forceOpen: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
      currentCompany: 'main/getCurrentCompany',
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
    isYourCompany() {
      return this.$route.params['contactId'] == this.getCompanyId
    },
    isEdit() {
      return this.$route.params['contactId']
    },
  },
  data() {
    return {
      showModal: false,
      contact: {},
      shareInvoicesState: false,
      defaultOpenerRoute: 'Contacts',
      hasPendingChanges: false,
      loading: false,
    }
  },
  watch: {
    forceOpen(val) {
      this.showModal = val
    },
    $route: {
      immediate: true,
      handler(to) {
        this.loading = true
        this.showModal = this.showModalOn.includes(to.name) || this.forceOpen
        this.showModal ? document.body.classList.add('modal-on') : document.body.classList.remove('modal-on')

        if (this.$route.params['contactId']) {
          CustomerService.retrieve(this.$route.params['contactId'], { extended: 'True' }).then((response) => {
            this.contact = response.data
            this.loading = false
          })
        } else {
          this.setupData()
          this.loading = false
        }
      },
    },
  },
  created() {
    window.addEventListener('keydown', this.closeEventListener)
  },
  destroyed() {
    window.removeEventListener('keydown', this.closeEventListener)
  },
  methods: {
    setupData() {
      this.contact = {
        name: '',
        contact_name: '',
        email: '',
        phone_number: '',
        kvk: '',
        btw: '',
        is_customer: true,
        is_supplier: false,
        is_archived: false,
        address: '',
        postal_code: '',
        city: '',
        country: this.currentCompany.country,
        show_country_on_invoice: false,
        description: '',
        hourly_rate: null,
        default_km_rate: null,
        default_payment_term: null,
        owner_company: this.getCompanyId,
      }
    },
    closeEventListener(event) {
      if (this.showModal && event.key === 'Escape') this.closeModal()
    },
    close() {
      this.shareInvoicesState = false
      this.$emit('cancel')
      this.closeModal()
    },
    onContactCreated(val) {
      this.$emit('created', val)
      this.closeModal()
    },
    onContactUpdated(val) {
      this.$emit('updated', val)
      this.closeModal()
    },
    closeModal() {
      if (this.forceOpen) {
        this.showModal = false
        this.$emit('cancel')
        document.body.classList.remove('modal-on')
      } else {
        if (this.$route.name !== this.defaultOpenerRoute) this.$router.push({ name: this.defaultOpenerRoute })
      }
    },
    toggleShareInvoicesState() {
      this.shareInvoicesState = !this.shareInvoicesState
    },
    deleteContact() {
      let contactId = this.$route.params['contactId']

      CustomerService.destroy(contactId)
        .then(() => {
          this.$emit('deleted', contactId)
          this.$store.commit('setAlert', {
            title: this.$gettext('Contact deleted'),
            text: this.$gettext('The contact was deleted correctly.'),
            variant: 'success',
            duration: 4,
          })
          this.$router.push({ name: this.defaultOpenerRoute })
        })
        .catch((response) => {
          if (response.status === 404) {
            this.$router.push({ name: this.defaultOpenerRoute })
          } else {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error deleting contact'),
              text: this.$gettext(
                'This customer cannot be deleted, only archived. Only customers that have no invoices, quotations, costs, hours, recurrent invoices attached to them can be deleted.'
              ),
              variant: 'danger',
              duration: 4,
            })
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.header-badge {
  margin-right: 10px;
  margin-left: 20px;
  font-size: 14px;
}
</style>
